import { z } from "zod";
import { actorIdentity, actorIdentitySchema, requireCapability, requireTenant, } from "./authority.js";
import { canonicalJsonSchema, canonicalSha256, } from "./canonicalize.js";
import { evidenceBindingSchema, protocolIdSchema, protocolTimestampSchema, protocolVersionIdSchema, protocolVersionSchema, sha256DigestSchema, verificationStatusSchema, } from "./identifiers.js";
import { assertCurrentVerdictReceipt } from "./verdict.js";
import { resolveStoredVerdict } from "../verdict-law.js";
export const learningProposalTargetSchema = z
    .object({
    ref: protocolIdSchema,
    baseVersion: protocolVersionIdSchema,
    baseDigest: sha256DigestSchema,
    successorVersion: protocolVersionIdSchema,
})
    .strict();
export const learningProposalSchema = z
    .object({
    protocolVersion: protocolVersionSchema,
    id: protocolIdSchema,
    tenantId: protocolIdSchema,
    sourceReceipt: z
        .object({
        id: protocolIdSchema,
        payloadDigest: sha256DigestSchema,
        binding: evidenceBindingSchema,
        aggregateStatus: verificationStatusSchema,
        determinationSetDigest: sha256DigestSchema,
        signingKeyId: protocolIdSchema,
    })
        .strict(),
    proposalKind: z.enum(["contract_successor", "policy_successor"]),
    target: learningProposalTargetSchema,
    proposal: canonicalJsonSchema,
    proposalDigest: sha256DigestSchema,
    proposedBy: actorIdentitySchema,
    proposedAt: protocolTimestampSchema,
    state: z.literal("PROPOSED"),
    currentTruthMutationAllowed: z.literal(false),
})
    .strict()
    .superRefine((value, context) => {
    if (value.target.baseVersion === value.target.successorVersion) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["target", "successorVersion"],
            message: "learning must propose a new successor version",
        });
    }
});
export const learningSourceKindSchema = z.enum([
    "signed_receipt",
    "admitted_evidence_metadata",
    "authorized_human_correction",
    "appeal_record",
    "downstream_effect_outcome",
    "raw_transcript_frequency",
]);
export const verifiedLearningSourceSchema = z
    .object({
    id: protocolIdSchema,
    kind: learningSourceKindSchema,
    tenantId: protocolIdSchema,
    binding: evidenceBindingSchema.nullable(),
    sourceDigest: sha256DigestSchema,
    admitted: z.boolean(),
    authorizedBy: actorIdentitySchema.nullable(),
    status: z.enum(["VERIFIED", "REJECTED", "HYPOTHESIS_ONLY"]),
    calibrationRole: z.enum(["positive", "negative", "false_positive", "neutral"]),
    protectedActivationAllowed: z.literal(false),
    rationale: z.string().trim().min(1),
})
    .strict()
    .superRefine((value, context) => {
    if (value.kind === "raw_transcript_frequency" && value.status !== "HYPOTHESIS_ONLY") {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["status"],
            message: "raw transcript frequency is hypothesis-only and cannot promote criteria",
        });
    }
    if (value.kind !== "raw_transcript_frequency" && value.status === "HYPOTHESIS_ONLY") {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["status"],
            message: "verified learning sources must carry a verified or rejected outcome",
        });
    }
    if (value.kind === "authorized_human_correction" && !value.authorizedBy) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["authorizedBy"],
            message: "human corrections require an authorized actor",
        });
    }
    if (value.kind === "downstream_effect_outcome" && !value.binding) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["binding"],
            message: "downstream learning outcomes require causal attempt binding",
        });
    }
    if (value.status === "VERIFIED" && !value.admitted) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["admitted"],
            message: "verified learning sources must be admitted",
        });
    }
});
export const learningSourceSetReportSchema = z
    .object({
    protocolVersion: protocolVersionSchema,
    tenantId: protocolIdSchema,
    promotionEligibleSourceKinds: z.array(learningSourceKindSchema),
    hypothesisOnlySourceKinds: z.array(learningSourceKindSchema),
    calibrationSourceIds: z.array(protocolIdSchema),
    negativeOutcomeSourceIds: z.array(protocolIdSchema),
    falsePositiveSourceIds: z.array(protocolIdSchema),
    protectedActivationAllowed: z.literal(false),
    reportDigest: sha256DigestSchema,
})
    .strict();
function sortedUnique(values) {
    return [...new Set(values)].sort();
}
export function summarizeVerifiedLearningSources(input) {
    const sources = input.sources.map((source) => verifiedLearningSourceSchema.parse(source));
    for (const source of sources) {
        if (source.tenantId !== input.tenantId) {
            throw new Error(`learning source ${source.id} belongs to tenant ${source.tenantId}`);
        }
    }
    const promotionEligible = sources.filter((source) => source.kind !== "raw_transcript_frequency" && source.status === "VERIFIED");
    const withoutDigest = {
        protocolVersion: "excellent.universal-verification.v1",
        tenantId: input.tenantId,
        promotionEligibleSourceKinds: sortedUnique(promotionEligible.map((source) => source.kind)),
        hypothesisOnlySourceKinds: sortedUnique(sources.filter((source) => source.status === "HYPOTHESIS_ONLY").map((source) => source.kind)),
        calibrationSourceIds: sources
            .filter((source) => source.calibrationRole !== "neutral")
            .map((source) => source.id)
            .sort(),
        negativeOutcomeSourceIds: sources
            .filter((source) => source.calibrationRole === "negative")
            .map((source) => source.id)
            .sort(),
        falsePositiveSourceIds: sources
            .filter((source) => source.calibrationRole === "false_positive")
            .map((source) => source.id)
            .sort(),
        protectedActivationAllowed: false,
    };
    return learningSourceSetReportSchema.parse({
        ...withoutDigest,
        reportDigest: canonicalSha256(withoutDigest, input.sha256),
    });
}
export function createLearningProposal(input) {
    const receipt = assertCurrentVerdictReceipt(input.receipt);
    const tenantId = receipt.payload.binding.tenantId;
    requireTenant(input.actor, tenantId);
    requireCapability(input.actor, "learning:propose");
    const proposalDigest = canonicalSha256({
        proposalKind: input.proposalKind,
        target: input.target,
        proposal: input.proposal,
    }, input.sha256);
    return learningProposalSchema.parse({
        protocolVersion: "excellent.universal-verification.v1",
        id: input.id,
        tenantId,
        sourceReceipt: {
            id: receipt.payload.id,
            payloadDigest: receipt.payloadDigest,
            binding: receipt.payload.binding,
            // READ TIME, at the instant this proposal is MINTED, and frozen there.
            //
            // `sourceReceipt` is a flat PROJECTION of the receipt — id, digests, binding,
            // verdict — and it deliberately carries no `determinations`, so it carries no
            // deadline either. That makes this the LAST point at which a PENDING source can
            // still be resolved: every later reader of a stored proposal (the console's
            // "Verified source" column and `learningSourceCalibration`) has only this string
            // and no way to ask the law what it means. Copying the raw
            // `receipt.payload.aggregateStatus` here would freeze "PENDING" into an
            // append-only record and every future reader would be told the wait is still on.
            //
            // `proposedAt` is the clock, not a fresh `new Date()`: the proposal is a record of
            // one decision at one instant, and its own timestamp is what that instant IS —
            // the same discipline the enforcement disposition uses with `decidedAt`.
            // `resolveStoredVerdict` returns a non-PENDING aggregate unchanged, so for the 81
            // receipts in the store today this is a provable no-op.
            aggregateStatus: resolveStoredVerdict({
                verdict: receipt.payload,
                now: input.proposedAt,
            }).status,
            determinationSetDigest: receipt.payload.determinationSetDigest,
            signingKeyId: receipt.signature.keyId,
        },
        proposalKind: input.proposalKind,
        target: input.target,
        proposal: input.proposal,
        proposalDigest,
        proposedBy: actorIdentity(input.actor),
        proposedAt: input.proposedAt,
        state: "PROPOSED",
        currentTruthMutationAllowed: false,
    });
}
